import { JsonUtility } from '../../app/jsonUtility.js';
import type {
    PreprocessedTemplate, TemplatePlaceholder, SlottedTemplate, SlotPlaceholder,
    JsonKeyPlaceholder, JsonArrayPlaceholder, JsonArraySkipPlaceholder, ReplacementMapping
} from '../../model/index.js';

/**
 * Purpose:
 * Serializes a PreprocessedTemplate to its canonical artifact JSON shape — the complete capitalized form
 * (OriginalContent, Placeholders, SlottedTemplates+Slots, Json*Placeholders, ReplacementMappings) that
 * both the api.json PreProcess emission and the OCI ".ociartifacts/preprocessed/" layer ship, and that
 * DeserializePreProcessMetadata inverts. Loader-owned (in core Assembler) so the api emission can reach it
 * without a test-project dependency; AssemblerQuality's SavePreProcessMetaData.SerializeTemplate delegates
 * here so there is ONE source of truth for the Preprocessed artifact bytes across both transports.
 * Mirrors csharp Assembler/PreProcess/Loader/SerializePreProcessMetadata.cs.
 *
 * Keep byte-shape-aligned with the deserializer: any field/array change here must be mirrored there.
 */
export class SerializePreProcessMetadata {
    /**
     * Serializes a single PreprocessedTemplate to a standalone JSON object string (the complete
     * capitalized shape, no AppSite dictionary wrapper). Used to build the api.json opaque
     * "Preprocessed" artifact and the ".ociartifacts/preprocessed/*" sidecars.
     */
    static serializeTemplate(template: PreprocessedTemplate): string {
        const lines: string[] = [];
        SerializePreProcessMetadata.writeTemplate(lines, template, 0);
        return lines.join('\n');
    }

    private static writeTemplate(lines: string[], template: PreprocessedTemplate, indentSpaces: number): void {
        const indent = ' '.repeat(indentSpaces);
        lines.push(`${indent}{`);
        SerializePreProcessMetadata.writeTemplateBody(lines, template, indentSpaces + 2, indentSpaces + 4);
        lines.push(`${indent}}`);
    }

    private static writeTemplateBody(lines: string[], template: PreprocessedTemplate, indentSpaces: number, itemIndentSpaces: number): void {
        const indent = ' '.repeat(indentSpaces);
        lines.push(`${indent}"OriginalContent": "${JsonUtility.escapeJson(template.originalContent)}"`);

        if (template.actualTemplateName) {
            lines[lines.length - 1] += ',';
            lines.push(`${indent}"ActualTemplateName": "${JsonUtility.escapeJson(template.actualTemplateName)}"`);
        }

        if (template.placeholders && template.placeholders.length > 0) {
            lines[lines.length - 1] += ',';
            SerializePreProcessMetadata.writeJsonArray(lines, `${indent}"Placeholders": [`, template.placeholders, itemIndentSpaces, SerializePreProcessMetadata.writePlaceholder);
        }

        if (template.slottedTemplates && template.slottedTemplates.length > 0) {
            lines[lines.length - 1] += ',';
            SerializePreProcessMetadata.writeJsonArray(lines, `${indent}"SlottedTemplates": [`, template.slottedTemplates, itemIndentSpaces, SerializePreProcessMetadata.writeSlottedTemplate);
        }

        if (template.jsonKeyPlaceholders && template.jsonKeyPlaceholders.length > 0) {
            lines[lines.length - 1] += ',';
            SerializePreProcessMetadata.writeJsonArray(lines, `${indent}"JsonKeyPlaceholders": [`, template.jsonKeyPlaceholders, itemIndentSpaces, SerializePreProcessMetadata.writeJsonKeyPlaceholder);
        }

        if (template.jsonSkipPlaceholders && template.jsonSkipPlaceholders.length > 0) {
            lines[lines.length - 1] += ',';
            SerializePreProcessMetadata.writeJsonArray(lines, `${indent}"JsonSkipPlaceholders": [`, template.jsonSkipPlaceholders, itemIndentSpaces, SerializePreProcessMetadata.writeJsonKeyPlaceholder);
        }

        if (template.jsonArrayPlaceholders && template.jsonArrayPlaceholders.length > 0) {
            lines[lines.length - 1] += ',';
            SerializePreProcessMetadata.writeJsonArray(lines, `${indent}"JsonArrayPlaceholders": [`, template.jsonArrayPlaceholders, itemIndentSpaces, SerializePreProcessMetadata.writeJsonArrayPlaceholder);
        }

        if (template.jsonArraySkipPlaceholders && template.jsonArraySkipPlaceholders.length > 0) {
            lines[lines.length - 1] += ',';
            SerializePreProcessMetadata.writeJsonArray(lines, `${indent}"JsonArraySkipPlaceholders": [`, template.jsonArraySkipPlaceholders, itemIndentSpaces, SerializePreProcessMetadata.writeJsonArraySkipPlaceholder);
        }

        if (template.replacementMappings && template.replacementMappings.length > 0) {
            lines[lines.length - 1] += ',';
            SerializePreProcessMetadata.writeJsonArray(lines, `${indent}"ReplacementMappings": [`, template.replacementMappings, itemIndentSpaces, SerializePreProcessMetadata.writeReplacementMapping);
        }
    }

    private static writeJsonArray<T>(lines: string[], header: string, items: T[], indentSpaces: number, writer: (lines: string[], item: T, indentSpaces: number) => void): void {
        const closingIndent = header.match(/^(\s*)/)?.[1] ?? '';
        lines.push(header);
        items.forEach((item, index) => {
            writer(lines, item, indentSpaces);
            if (index < items.length - 1) {
                lines[lines.length - 1] += ',';
            }
        });
        lines.push(`${closingIndent}]`);
    }

    private static writePlaceholder(lines: string[], placeholder: TemplatePlaceholder, indentSpaces: number): void {
        const indent = ' '.repeat(indentSpaces);
        lines.push(`${indent}{`);
        lines.push(`${indent}  "Name": "${JsonUtility.escapeJson(placeholder.name)}",`);
        lines.push(`${indent}  "StartIndex": ${placeholder.startIndex},`);
        lines.push(`${indent}  "EndIndex": ${placeholder.endIndex},`);
        lines.push(`${indent}  "FullMatch": "${JsonUtility.escapeJson(placeholder.fullMatch)}",`);
        lines.push(`${indent}  "TemplateKey": "${JsonUtility.escapeJson(placeholder.templateKey)}",`);

        // Nested placeholders
        lines.push(`${indent}  "NestedPlaceholders": [`);
        if (placeholder.nestedPlaceholders) {
            placeholder.nestedPlaceholders.forEach((nested, i) => {
                SerializePreProcessMetadata.writePlaceholder(lines, nested, indentSpaces + 4);
                if (i < placeholder.nestedPlaceholders.length - 1) {
                    lines[lines.length - 1] += ',';
                }
            });
        }
        lines.push(`${indent}  ],`);

        // Nested slots
        lines.push(`${indent}  "NestedSlots": [`);
        if (placeholder.nestedSlots) {
            placeholder.nestedSlots.forEach((slot, i) => {
                SerializePreProcessMetadata.writeSlotPlaceholder(lines, slot, indentSpaces + 4);
                if (i < placeholder.nestedSlots.length - 1) {
                    lines[lines.length - 1] += ',';
                }
            });
        }
        lines.push(`${indent}  ]`);
        lines.push(`${indent}}`);
    }

    private static writeSlottedTemplate(lines: string[], slotted: SlottedTemplate, indentSpaces: number): void {
        const indent = ' '.repeat(indentSpaces);
        lines.push(`${indent}{`);
        lines.push(`${indent}  "Name": "${JsonUtility.escapeJson(slotted.name)}",`);
        lines.push(`${indent}  "StartIndex": ${slotted.startIndex},`);
        lines.push(`${indent}  "EndIndex": ${slotted.endIndex},`);
        lines.push(`${indent}  "FullMatch": "${JsonUtility.escapeJson(slotted.fullMatch)}",`);
        lines.push(`${indent}  "InnerContent": "${JsonUtility.escapeJson(slotted.innerContent)}",`);
        lines.push(`${indent}  "TemplateKey": "${JsonUtility.escapeJson(slotted.templateKey)}",`);

        // Slots
        lines.push(`${indent}  "Slots": [`);
        if (slotted.slots) {
            slotted.slots.forEach((slot, i) => {
                SerializePreProcessMetadata.writeSlotPlaceholder(lines, slot, indentSpaces + 4);
                if (i < slotted.slots.length - 1) {
                    lines[lines.length - 1] += ',';
                }
            });
        }
        lines.push(`${indent}  ]`);
        lines.push(`${indent}}`);
    }

    private static writeSlotPlaceholder(lines: string[], slot: SlotPlaceholder, indentSpaces: number): void {
        const indent = ' '.repeat(indentSpaces);
        lines.push(`${indent}{`);
        lines.push(`${indent}  "Number": "${JsonUtility.escapeJson(slot.number)}",`);
        lines.push(`${indent}  "StartIndex": ${slot.startIndex},`);
        lines.push(`${indent}  "EndIndex": ${slot.endIndex},`);
        lines.push(`${indent}  "Content": "${JsonUtility.escapeJson(slot.content)}",`);
        lines.push(`${indent}  "SlotKey": "${JsonUtility.escapeJson(slot.slotKey)}",`);
        lines.push(`${indent}  "OpenTag": "${JsonUtility.escapeJson(slot.openTag)}",`);
        lines.push(`${indent}  "CloseTag": "${JsonUtility.escapeJson(slot.closeTag)}",`);
        lines.push(`${indent}  "HasNestedPlaceholders": ${slot.hasNestedPlaceholders ? 'true' : 'false'},`);
        lines.push(`${indent}  "HasNestedSlottedTemplates": ${slot.hasNestedSlottedTemplates ? 'true' : 'false'},`);
        lines.push(`${indent}  "RequiresNestedProcessing": ${slot.requiresNestedProcessing ? 'true' : 'false'},`);

        // Nested placeholders
        lines.push(`${indent}  "NestedPlaceholders": [`);
        if (slot.nestedPlaceholders) {
            slot.nestedPlaceholders.forEach((nested, i) => {
                SerializePreProcessMetadata.writePlaceholder(lines, nested, indentSpaces + 4);
                if (i < slot.nestedPlaceholders.length - 1) {
                    lines[lines.length - 1] += ',';
                }
            });
        }
        lines.push(`${indent}  ],`);

        // Nested slotted templates
        lines.push(`${indent}  "NestedSlottedTemplates": [`);
        if (slot.nestedSlottedTemplates) {
            slot.nestedSlottedTemplates.forEach((nestedSlotted, i) => {
                SerializePreProcessMetadata.writeSlottedTemplate(lines, nestedSlotted, indentSpaces + 4);
                if (i < slot.nestedSlottedTemplates.length - 1) {
                    lines[lines.length - 1] += ',';
                }
            });
        }
        lines.push(`${indent}  ],`);

        // Nested slots
        lines.push(`${indent}  "NestedSlots": [`);
        if (slot.nestedSlots) {
            slot.nestedSlots.forEach((nestedSlot, i) => {
                SerializePreProcessMetadata.writeSlotPlaceholder(lines, nestedSlot, indentSpaces + 4);
                if (i < slot.nestedSlots.length - 1) {
                    lines[lines.length - 1] += ',';
                }
            });
        }
        lines.push(`${indent}  ]`);
        lines.push(`${indent}}`);
    }

    private static writeJsonKeyPlaceholder(lines: string[], placeholder: JsonKeyPlaceholder, indentSpaces: number): void {
        const indent = ' '.repeat(indentSpaces);
        lines.push(`${indent}{`);
        lines.push(`${indent}  "KeyName": "${JsonUtility.escapeJson(placeholder.keyName)}",`);
        lines.push(`${indent}  "StartIndex": ${placeholder.startIndex},`);
        lines.push(`${indent}  "EndIndex": ${placeholder.endIndex},`);
        lines.push(`${indent}  "FullMatch": "${JsonUtility.escapeJson(placeholder.fullMatch)}"`);
        lines.push(`${indent}}`);
    }

    private static writeJsonArrayPlaceholder(lines: string[], placeholder: JsonArrayPlaceholder, indentSpaces: number): void {
        const indent = ' '.repeat(indentSpaces);
        lines.push(`${indent}{`);
        lines.push(`${indent}  "ArrayName": "${JsonUtility.escapeJson(placeholder.arrayName)}",`);
        lines.push(`${indent}  "StartIndex": ${placeholder.startIndex},`);
        lines.push(`${indent}  "EndIndex": ${placeholder.endIndex},`);
        lines.push(`${indent}  "FullMatch": "${JsonUtility.escapeJson(placeholder.fullMatch)}",`);
        lines.push(`${indent}  "BlockContent": "${JsonUtility.escapeJson(placeholder.blockContent)}",`);
        lines.push(`${indent}  "NegativeBlockContent": "${JsonUtility.escapeJson(placeholder.negativeBlockContent || '')}",`);
        lines.push(`${indent}  "FullMatchWithNegative": "${JsonUtility.escapeJson(placeholder.fullMatchWithNegative || '')}",`);
        lines.push(`${indent}  "HasNegativeBlock": ${placeholder.hasNegativeBlock ? 'true' : 'false'},`);
        lines.push(`${indent}  "IsPairedNegative": ${placeholder.isPairedNegative ? 'true' : 'false'},`);

        // NestedKeys array
        const nestedKeys = placeholder.nestedKeys || [];
        lines.push(`${indent}  "NestedKeys": [${nestedKeys.map((k: string) => `"${JsonUtility.escapeJson(k)}"`).join(', ')}],`);

        // NestedArrays array
        const nestedArrays = placeholder.nestedArrays || [];
        lines.push(`${indent}  "NestedArrays": [${nestedArrays.map((a: string) => `"${JsonUtility.escapeJson(a)}"`).join(', ')}]`);
        lines.push(`${indent}}`);
    }

    private static writeJsonArraySkipPlaceholder(lines: string[], placeholder: JsonArraySkipPlaceholder, indentSpaces: number): void {
        const indent = ' '.repeat(indentSpaces);
        lines.push(`${indent}{`);
        lines.push(`${indent}  "ArrayName": "${JsonUtility.escapeJson(placeholder.arrayName)}",`);
        lines.push(`${indent}  "StartIndex": ${placeholder.startIndex},`);
        lines.push(`${indent}  "EndIndex": ${placeholder.endIndex},`);
        lines.push(`${indent}  "FullMatch": "${JsonUtility.escapeJson(placeholder.fullMatch)}"`);
        lines.push(`${indent}}`);
    }

    private static writeReplacementMapping(lines: string[], mapping: ReplacementMapping, indentSpaces: number): void {
        const indent = ' '.repeat(indentSpaces);
        lines.push(`${indent}{`);
        lines.push(`${indent}  "StartIndex": ${mapping.startIndex},`);
        lines.push(`${indent}  "EndIndex": ${mapping.endIndex},`);
        lines.push(`${indent}  "OriginalText": "${JsonUtility.escapeJson(mapping.originalText)}",`);
        lines.push(`${indent}  "ReplacementText": "${JsonUtility.escapeJson(mapping.replacementText)}",`);

        if (mapping.targetTemplateName) {
            lines.push(`${indent}  "Type": "${mapping.type}",`);
            lines.push(`${indent}  "TargetTemplateName": "${JsonUtility.escapeJson(mapping.targetTemplateName)}"`);
        } else {
            lines.push(`${indent}  "Type": "${mapping.type}"`);
        }

        lines.push(`${indent}}`);
    }
}
